import React, { useEffect, useState, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { Modal } from 'react-bootstrap'
import { t } from 'i18next'
import NavBar from './components/NavBar/NavBar'
import Loading from 'Components/Loading'
import ProductDetail from './components/ProductDetail/ProductDetail'
import MediaScreensaver from './components/MediaScreensaver'
import { useCustomerDisplay } from './context/CustomerDisplayContext'
import { validatorCompany } from './utils/validatorCompany'

export default function CustomerDisplayLayout({ children }) {
  const location = useLocation()
  const {
    company,
    isChangeCompany,
    channel,
    isProductDetailOpen,
    setIsProductDetailOpen,
    productDetail,
    isSleepMode,
  } = useCustomerDisplay()
  const companySelected = validatorCompany(company)
  const [showModalFullScreen, setShowModalFullScreen] = useState(false)
  const sleepModeCountRef = useRef(0)

  // Increment counter each time sleep mode is activated to force fresh content
  useEffect(() => {
    if (isSleepMode) {
      sleepModeCountRef.current += 1
    }
  }, [isSleepMode])

  // Detect fullscreen changes and update modal visibility
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullScreen = !!document.fullscreenElement
      setShowModalFullScreen(!isFullScreen)

      // Notify main app when entering fullscreen
      if (isFullScreen && channel) {
        channel.postMessage({ isFullScreen: true })
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)

    // Check initial state
    handleFullscreenChange()

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [channel])

  // Fullscreen enter logic - triggers on click or keydown
  useEffect(() => {
    const enterFullscreen = event => {
      if (event.type === 'click' || event.key === 'Enter') {
        const elem = document.documentElement
        if (elem.requestFullscreen) elem.requestFullscreen()
        else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen()
        else if (elem.msRequestFullscreen) elem.msRequestFullscreen()

        if (channel) {
          channel.postMessage({ isFullScreen: true })
        }
        document.removeEventListener('click', enterFullscreen)
        document.removeEventListener('keydown', enterFullscreen)
      }
    }

    document.addEventListener('click', enterFullscreen)
    document.addEventListener('keydown', enterFullscreen)

    return () => {
      document.removeEventListener('click', enterFullscreen)
      document.removeEventListener('keydown', enterFullscreen)
    }
  }, [channel])

  // Send user activity to main app via BroadcastChannel
  useEffect(() => {
    const notifyActivity = () => {
      if (channel) {
        channel.postMessage({ userActivity: true })
      }
    }

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
    events.forEach(e => window.addEventListener(e, notifyActivity))

    return () => {
      events.forEach(e => window.removeEventListener(e, notifyActivity))
    }
  }, [channel])

  // Check if currently on the idle/background route
  const isIdleRoute = location.pathname === '/customer_display'

  // Handle product detail modal close
  const handleCloseProductDetail = () => {
    if (channel) {
      channel.postMessage({
        isProductDetail: false,
        isCloseDetailProduct: true,
      })
    }
    setIsProductDetailOpen(false)
  }

  // Render content based on company change loading state
  if (isChangeCompany) {
    return <Loading full />
  }

  return (
    <>
      <div className="layout" data-testid="container-customer-display-main">
        {!isIdleRoute && (
          <div className="navbar-fixed" data-testid="container-navbar-main">
            <NavBar company={companySelected} />
          </div>
        )}
        <div className="content" data-testid="container-main-content">
          {children}
        </div>
      </div>

      {/* Fullscreen prompt modal */}
      <Modal
        show={showModalFullScreen}
        centered
        className="container-modal-full-screen"
        data-testid="modal-full-screen">
        <h2 data-testid="text-full-screen-message">
          {t('CUSTOMER_DISPLAY.MESSAGE_FULL_SCREEN')}
        </h2>
      </Modal>

      {/* Product Detail Modal */}
      <Modal
        show={isProductDetailOpen}
        fullscreen={true}
        onHide={handleCloseProductDetail}
        className="modal-product-detail-fullscreen"
        data-testid="modal-product-detail-fullscreen">
        <div
          className="layout"
          data-testid="container-customer-display-layout-modal">
          <div className="navbar-fixed" data-testid="container-navbar-fixed">
            <NavBar company={companySelected} />
          </div>
          <div
            className="content"
            data-testid="container-product-detail-content">
            {productDetail && (
              <ProductDetail
                product={productDetail}
                handleCloseModal={handleCloseProductDetail}
              />
            )}
          </div>
        </div>
      </Modal>

      {/* Sleep Mode Overlay - shows media screensaver when inactive */}
      {isSleepMode && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
          }}
          data-testid="sleep-mode-overlay">
          <MediaScreensaver key={sleepModeCountRef.current} />
        </div>
      )}
    </>
  )
}
